import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Toaster, type ToastItem, type ToastTone } from '@/components/Toaster/Toaster';

import { ToastContext, type ToastApi, type ToastOptions } from './toast-context';

const VISIBLE_MS = 5000;
const EXIT_MS = 200;
const MAX_OPEN = 3;

/** Holds the open toasts and their timers, and renders the `Toaster` over the app. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((open) => open.filter((toast) => toast.id !== id));
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      clearTimeout(timers.current.get(id));
      setToasts((open) =>
        open.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)),
      );
      timers.current.set(
        id,
        setTimeout(() => remove(id), EXIT_MS),
      );
    },
    [remove],
  );

  const show = useCallback(
    (tone: ToastTone, { title, description }: ToastOptions) => {
      const id = nextId.current++;
      setToasts((open) => [...open, { id, tone, title, description }].slice(-MAX_OPEN));
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), VISIBLE_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => clearTimeout(timer));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (options) => show('success', options),
      info: (options) => show('info', options),
      warning: (options) => show('warning', options),
      error: (options) => show('error', options),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Toaster onDismiss={dismiss} toasts={toasts} />
    </ToastContext.Provider>
  );
}
